import { AlertDialogDescription, AlertDialogLabel, AlertDialogOverlay } from '@reach/alert-dialog';
import { navigate } from 'gatsby';
import React, { FC, useMemo, useRef } from 'react';
import showdown from 'showdown';
import styled from 'styled-components';

import BasicText from '@ui/atoms/BasicText';
import Button from '@ui/atoms/Button';
import FlexColumn from '@ui/atoms/FlexColumn';
import SubheadingText from '@ui/atoms/SubheadingText';
import TextButton from '@ui/atoms/TextButton';
import WYSIWYGContent, { IWYSIWYGContentProps } from '@ui/atoms/WYSIWYGContent';

import spaced from '@ui/styled/Spaced';

import { ICurrentPolicyNode } from '@data/consent';

import Legal from '@assets/vectors/legal.inline.svg';

import theme from '@theme/FolxTheme';

export interface ITermsOfServiceDialogContent {
	tosHeader: string;
	tosDescription: string;
	tosDisclaimer: string;
	tosSupport: string;
	agreeLabel: string;
	disagreeLabel: string;
}

interface ITermsOfServiceDialogProps {
	dialogContent: ITermsOfServiceDialogContent;
	consentPolicy: ICurrentPolicyNode;
	onAgree: () => void;
}

const SpacedWysiwyg = spaced<IWYSIWYGContentProps>(WYSIWYGContent);

const DialogWrapper = styled.div`
	background: ${theme.colors.white};
	border-radius: ${theme.radius.md};
	margin: ${theme.spacing.lg} ${theme.spacing.sm};
	padding: ${theme.spacing.smmd};

	svg {
		align-self: center;
	}

	button {
		align-self: center;
		min-width: 18rem;
	}

	@media ${theme.responsive.breakpoints.mobileL} {
		margin: ${theme.spacing.lg};
	}

	@media ${theme.responsive.breakpoints.tablet} {
		margin: ${theme.spacing.xxxxxl} auto ${theme.spacing.xxxl};
		max-width: 72rem;
	}
`;

const TermsOfServiceDialog: FC<ITermsOfServiceDialogProps> = ({
	consentPolicy,
	dialogContent,
	onAgree = () => {
		return;
	},
}) => {
	const cancelRef = useRef();

	const { tosHeader, tosDescription, tosDisclaimer, tosSupport, agreeLabel, disagreeLabel } =
		dialogContent;

	const consentSummary = useMemo(() => {
		const converter = new showdown.Converter();
		return converter.makeHtml(consentPolicy.contentSummary);
	}, [consentPolicy.contentSummary]);

	return (
		<AlertDialogOverlay
			leastDestructiveRef={cancelRef}
			style={{ backgroundColor: theme.colors.modalGray }}>
			<DialogWrapper>
				<FlexColumn gap="sm">
					<Legal />
					<AlertDialogLabel>
						<SubheadingText>{tosHeader}</SubheadingText>
					</AlertDialogLabel>
					<AlertDialogDescription>
						<FlexColumn gap="xs">
							<BasicText>{tosDescription}</BasicText>
							<WYSIWYGContent dangerouslySetInnerHTML={{ __html: consentSummary }} />
							<WYSIWYGContent
								textVariant="fineprint"
								dangerouslySetInnerHTML={{ __html: tosDisclaimer }}
							/>
						</FlexColumn>
					</AlertDialogDescription>
					<Button label={agreeLabel} variant="secondary" onClick={onAgree} />
					<TextButton
						variant="text"
						underline
						label={disagreeLabel}
						onClick={() => {
							navigate('/logout');
						}}
					/>
					<SpacedWysiwyg
						marginTop="md"
						textVariant="fineprint"
						dangerouslySetInnerHTML={{ __html: tosSupport }}
					/>
				</FlexColumn>
			</DialogWrapper>
		</AlertDialogOverlay>
	);
};

export default styled(TermsOfServiceDialog)``;
